/**
 * features/journal/buildJournalDraft.ts
 *
 * Generates a pre-filled JournalDraft from canonical SymbolContext + AI thesis.
 * The trader only needs to add: decision, reason, execution quality, emotion, outcome.
 * Everything else is auto-filled from the deterministic engine truth.
 *
 * This is a pure function — no side effects, no API calls.
 */

import { JournalDraft } from '../../types';
import { SymbolContext } from '../../services/buildSymbolContext';
import { ValidatedAIResponse } from '../../ai/responseSchema';
import { getSessionPhase } from '../../utils/session';

// ─── Title generator ──────────────────────────────────────────────────────────

function buildTitle(ctx: SymbolContext, thesis: ValidatedAIResponse | null): string {
  const sym = ctx.symbol;
  const truth = ctx.setupTruth;

  const setupLabel =
    truth.actionableSetupType ??
    truth.displaySetupType ??
    thesis?.marketState ??
    null;

  if (!setupLabel) return `${sym} — No active setup`;

  const maturityLabel =
    truth.maturity === 'actionable' ? 'actionable' :
    truth.maturity === 'confirmed'  ? 'confirmed'  : 'watch';

  const dirLabel = truth.actionableSetupDir
    ? ` (${truth.actionableSetupDir.toUpperCase()})`
    : '';

  if (!ctx.setupActionable) {
    return `${sym} — ${setupLabel.replace(/_/g, ' ')}${dirLabel} — WAIT`;
  }

  return `${sym} — ${setupLabel.replace(/_/g, ' ')}${dirLabel} — ${maturityLabel}`;
}

// ─── Actionability narrative ──────────────────────────────────────────────────
// Deterministic text — no AI call. Built from setupTruth fields.

function buildWhyActionable(ctx: SymbolContext): string {
  const truth = ctx.setupTruth;

  if (ctx.setupActionable) {
    const parts: string[] = [];
    if (truth.stateTransitionConfirmed) parts.push('state transition confirmed');
    if (!truth.countertrend) parts.push('aligned with trend');
    if (truth.maturity === 'actionable') parts.push('setup fully mature');
    else if (truth.maturity === 'confirmed') parts.push('setup confirmed');
    return `Setup is actionable: ${parts.join(', ') || 'passed maturity and alignment gates'}.`;
  }

  const reasons: string[] = [];
  if (truth.maturity === 'developing') reasons.push('setup still developing');
  if (truth.alignment === 'countertrend-unconfirmed') reasons.push('countertrend-unconfirmed');
  if (!truth.stateTransitionConfirmed) reasons.push('state transition not confirmed');
  if (truth.confirmationMissing.length) {
    reasons.push(`missing: ${truth.confirmationMissing.join(', ')}`);
  }

  if (!reasons.length) return 'Setup did not pass the actionability gate.';
  return `Setup not actionable — ${reasons.join('; ')}.`;
}

// ─── Why it mattered ─────────────────────────────────────────────────────────
// Short narrative combining market context + setup type + thesis.

function buildWhyItMattered(ctx: SymbolContext, thesis: ValidatedAIResponse | null): string {
  const truth = ctx.setupTruth;
  const regime = ctx.regime?.label ?? 'unknown regime';
  const smcBias = ctx.smc?.bias ?? 'neutral';
  const setupLabel = truth.actionableSetupType ?? truth.displaySetupType ?? 'setup';

  const thesisFrag = thesis?.primaryThesis
    ? ` AI thesis: ${thesis.primaryThesis.substring(0, 100)}${thesis.primaryThesis.length > 100 ? '…' : ''}`
    : '';

  return `${ctx.symbol} — ${regime}, SMC ${smcBias} bias. ${setupLabel.replace(/_/g,' ')} pattern present.${thesisFrag}`;
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildJournalDraft(
  ctx:       SymbolContext,
  thesis:    ValidatedAIResponse | null,
  snapshotId?: string | null
): JournalDraft {
  const id = `jd_${ctx.symbol}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const truth = ctx.setupTruth;

  // Best setup for plan fields — prefer bestActionableSetup, fall back to topDisplaySetup
  const planSetup = ctx.bestActionableSetup ?? ctx.topDisplaySetup;

  return {
    id,
    createdAt: Date.now(),
    symbol: ctx.symbol,

    // Generated title
    title: buildTitle(ctx, thesis),

    // Market context
    sessionPhase:      getSessionPhase(),
    price:             ctx.curPrice,
    vwap:              ctx.curVWAP,
    rvol:              ctx.regime?.rvol ?? null,
    regime:            ctx.regime?.label ?? null,
    regimeConfidence:  ctx.regime?.confidence ?? null,
    smcBias:           ctx.smc?.bias ?? null,

    // Canonical setup truth — from ctx.setupTruth, never re-inferred
    setupActionable:          ctx.setupActionable,
    displaySetupType:         truth.displaySetupType,
    actionableSetupType:      truth.actionableSetupType,
    actionableSetupDir:       truth.actionableSetupDir,
    setupMaturity:            truth.maturity,
    setupAlignment:           truth.alignment,
    countertrend:             truth.countertrend,
    stateTransitionConfirmed: truth.stateTransitionConfirmed,
    confirmationMissing:      truth.confirmationMissing,

    // Best setup plan fields
    entry:          planSetup?.entry          ?? null,
    confirmation:   planSetup?.confirmation   ?? null,
    invalidation:   planSetup?.invalidation   ?? null,
    t1:             planSetup?.t1             ?? null,
    t2:             planSetup?.t2             ?? null,
    setupConfidence: planSetup?.confidence    ?? null,
    setupRationale:  planSetup?.rationale     ?? null,

    // AI thesis layer
    primaryThesis:  thesis?.primaryThesis   ?? null,
    marketState:    thesis?.marketState     ?? null,
    keyDrivers:     thesis?.keyDrivers      ?? [],
    uncertainty:    thesis?.uncertainty     ?? null,
    shouldTradeNow: thesis?.shouldTradeNow  ?? null,

    // Auto-generated deterministic summaries
    whyItMattered:  buildWhyItMattered(ctx, thesis),
    whyActionable:  buildWhyActionable(ctx),

    // Human input — all blank
    decision:        null,
    decisionReason:  '',
    executionQuality: null,
    emotionState:    null,
    traderNotes:     '',
    outcome:         'pending',
    outcomeNotes:    '',

    // Linkage
    researchSnapshotId: snapshotId ?? null,
    savedToJournal:     false,
  };
}
